export const CHANGE_RECORD_DIFF_KEY = 'changeRecordDiff';

export const CR_ROW = '__crRow';
export const CR_PREV = '__crPrev';

export type ChangeRecordRowKind = 'kept' | 'added' | 'removed';

export type ChangeRecordRowMark = 'added' | 'removed' | 'changed' | 'unchanged';

export const CHANGE_RECORD_KIND_COLUMN = {
  title: '行状态',
  key: '__changeRecordKind',
  dataIndex: '__changeRecordKind',
  propId: '__changeRecordKind',
  propType: 'changeRecordKind',
  controlType: 'changeRecordKind',
  width: 88,
  align: 'center',
  ellipsis: false,
  showFilter: false,
  resizable: false,
  fixed: 'left',
};

export type ChangeRecordDiffState = {
  enabled: boolean;
  prevData: Record<string, any> | null;
};

export type ChangeRecordFieldHint = {
  kind: 'changed' | 'prevMissing' | 'removed';
  text: string;
};

const SKIP_FIELD_COMPONENTS = new Set([
  'xmSubTable',
  'xmDiv',
  'xmForm',
  'xmTabs',
  'xmButton',
  'xmHidden',
  'xmFileUpload',
  'xmImgUpload',
  'HtmlEditor',
  'xmActionBar',
]);

export function isChangeRecordSkipField(componentKey?: string) {
  return SKIP_FIELD_COMPONENTS.has(String(componentKey || ''));
}

export function isEmptyDiffValue(value: unknown) {
  if (value == null || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

export function formatDiffValue(value: unknown) {
  if (isEmptyDiffValue(value)) {
    return '';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function diffValuesEqual(left: unknown, right: unknown) {
  if (isEmptyDiffValue(left) && isEmptyDiffValue(right)) {
    return true;
  }
  if (isEmptyDiffValue(left) || isEmptyDiffValue(right)) {
    return false;
  }
  if (typeof left === 'object' || typeof right === 'object') {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return false;
    }
  }
  return String(left) === String(right);
}

export function resolveFieldHint(current: unknown, prev: unknown): ChangeRecordFieldHint | null {
  if (diffValuesEqual(current, prev)) {
    return null;
  }
  if (isEmptyDiffValue(prev)) {
    return { kind: 'prevMissing', text: '无' };
  }
  if (isEmptyDiffValue(current)) {
    return { kind: 'removed', text: formatDiffValue(prev) };
  }
  return { kind: 'changed', text: formatDiffValue(prev) };
}

function asRowList(value: unknown): any[] {
  return Array.isArray(value) ? value : [];
}

function rowId(row: any, index: number, side: string) {
  const id = row?.id ?? row?.key;
  if (id != null && String(id) !== '') {
    return String(id);
  }
  return `${side}:${index}`;
}

function stripRowMeta(row: any): any {
  if (row == null || typeof row !== 'object') {
    return row;
  }
  const next = { ...row };
  delete next[CR_ROW];
  delete next[CR_PREV];
  if (Array.isArray(next.children)) {
    next.children = next.children.map((item: any) => stripRowMeta(item));
  }
  return next;
}

/** 按行 id 做两版并集：先上一版行序，再追加本版新增行 */
export function mergeSubTableRows(current: unknown, prev: unknown): any[] {
  const curList = asRowList(current);
  const prevList = asRowList(prev);
  const curMap = new Map<string, any>();
  const prevMap = new Map<string, any>();
  curList.forEach((row, index) => curMap.set(rowId(row, index, 'cur'), row));
  prevList.forEach((row, index) => prevMap.set(rowId(row, index, 'prev'), row));

  const result: any[] = [];
  prevList.forEach((prevRow, index) => {
    const id = rowId(prevRow, index, 'prev');
    const curRow = curMap.get(id);
    if (curRow) {
      const merged: any = {
        ...curRow,
        [CR_ROW]: 'kept',
        [CR_PREV]: stripRowMeta(prevRow),
      };
      if (asRowList(curRow.children).length || asRowList(prevRow.children).length) {
        merged.children = mergeSubTableRows(curRow.children, prevRow.children);
      }
      result.push(merged);
    } else {
      result.push(markTreeRow(prevRow, 'removed'));
    }
  });
  curList.forEach((curRow, index) => {
    const id = rowId(curRow, index, 'cur');
    if (!prevMap.has(id)) {
      result.push(markTreeRow(curRow, 'added'));
    }
  });
  return result;
}

function markTreeRow(row: any, kind: 'added' | 'removed') {
  const next: any = kind === 'removed'
    ? { ...row, [CR_ROW]: kind, [CR_PREV]: stripRowMeta(row) }
    : { ...row, [CR_ROW]: kind };
  if (asRowList(next.children).length) {
    next.children = next.children.map((item: any) => markTreeRow(item, kind));
  }
  return next;
}

export function applySubTableUnion(
  current: Record<string, any>,
  prev: Record<string, any> | null,
  subTableKeys: string[] | undefined,
) {
  if (!prev || !Array.isArray(subTableKeys) || subTableKeys.length === 0) {
    return current;
  }
  subTableKeys.forEach((key) => {
    if (!key) return;
    current[key] = mergeSubTableRows(current[key], prev[key]);
  });
  return current;
}

export function collectSubTableKeys(blocks: any[] | undefined, keys: string[] = []) {
  if (!blocks || !Array.isArray(blocks)) {
    return keys;
  }
  blocks.forEach((item) => {
    if (item?.componentKey === 'xmSubTable') {
      const key = item?.model?.key;
      if (key) keys.push(String(key));
    }
    if (item?.children) {
      collectSubTableKeys(item.children, keys);
    }
  });
  return keys;
}

export function isKeptChangeRecordRow(record: any) {
  return record?.[CR_ROW] === 'kept' && record?.[CR_PREV] != null && typeof record[CR_PREV] === 'object';
}

/** 只有做过两版并集的子表才带 __crRow；用料清单等未并集的表不能插行状态列 */
export function hasChangeRecordRowMark(rows: unknown): boolean {
  return asRowList(rows).some((row) => row != null && row[CR_ROW]);
}

function isSkipCompareColumn(column: any) {
  if (!column) return true;
  const type = column.propType || column.controlType;
  return type === 'operation'
    || type === 'orderNum'
    || type === 'columnsConfig'
    || type === 'changeRecordKind'
    || column.controlType === 'orderNum';
}

export function resolveChangeRecordRowMark(record: any, columns?: any[]): ChangeRecordRowMark {
  if (record?.[CR_ROW] === 'added') return 'added';
  if (record?.[CR_ROW] === 'removed') return 'removed';
  if (!isKeptChangeRecordRow(record)) return 'unchanged';
  const list = Array.isArray(columns) ? columns : [];
  const changed = list.some((column) => {
    if (isSkipCompareColumn(column)) return false;
    const propId = column.propId || column.dataIndex;
    if (!propId) return false;
    return !diffValuesEqual(record?.[propId], record[CR_PREV][propId]);
  });
  return changed ? 'changed' : 'unchanged';
}

export function resolveCellPrevHint(record: any, column: any) {
  if (!isKeptChangeRecordRow(record) || isSkipCompareColumn(column)) {
    return null;
  }
  const propId = column.propId || column.dataIndex || column.key;
  if (!propId) {
    return null;
  }
  return resolveFieldHint(record[propId], record[CR_PREV][propId]);
}
